import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

import { API_BASE } from "../config";

const AUTH_USER_STORAGE_KEY = 'authUser';

const persistAuthUser = (user) => {
  if (user) {
    localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(AUTH_USER_STORAGE_KEY);
  }
};

const readStoredAuthUser = () => {
  const raw = localStorage.getItem(AUTH_USER_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (error) {
    localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    return null;
  }
};

const getErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.error?.message ||
  error?.response?.data?.detail ||
  fallbackMessage;

// Create a stable axios instance outside the component
const createApiInstance = () => {
  const instance = axios.create({
    baseURL: `${API_BASE}/api`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add auth header to requests
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  return instance;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [businessSettings, setBusinessSettings] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [workspaceAccessError, setWorkspaceAccessError] = useState(null);

  // Create stable api instance
  const api = useMemo(() => createApiInstance(), []);

  // Setup response interceptor
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            try {
              const response = await axios.post(`${API_BASE}/api/auth/refresh`, null, {
                params: { refresh_token: refreshToken }
              });
              const { access_token, refresh_token: newRefreshToken } = response.data;
              localStorage.setItem('accessToken', access_token);
              localStorage.setItem('refreshToken', newRefreshToken);
              originalRequest.headers.Authorization = `Bearer ${access_token}`;
              return api(originalRequest);
            } catch (refreshError) {
              // Clear auth state on refresh failure
              localStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
              persistAuthUser(null);
              setUser(null);
              setBusinessSettings(null);
              setPermissions(null);
              setWorkspaceAccessError(null);
              return Promise.reject(refreshError);
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [api]);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await api.get('/settings/permissions/me');
      setPermissions(response.data);
      return response.data;
    } catch (error) {
      return null;
    }
  }, [api]);

  const fetchBusinessSettings = useCallback(async () => {
    try {
      const response = await api.get('/settings/business');
      setBusinessSettings(response.data);
      setWorkspaceAccessError(null);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        // Expected before first-time owner setup is completed.
        setBusinessSettings(null);
        setWorkspaceAccessError(null);
        return null;
      }
      if (error?.response?.status === 402 || error?.response?.status === 403) {
        setWorkspaceAccessError({
          status: error.response.status,
          message: getErrorMessage(error, 'Your workspace is temporarily unavailable.'),
        });
        setBusinessSettings(null);
        return null;
      }
      console.error('Failed to fetch business settings:', error);
      setWorkspaceAccessError({
        status: error?.response?.status || 500,
        message: getErrorMessage(error, 'Failed to load business settings.'),
      });
      return null;
    }
  }, [api]);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const response = await api.get('/auth/me');
        const currentUser = response.data;
        setUser(currentUser);
        persistAuthUser(currentUser);
        setWorkspaceAccessError(null);
        if (currentUser?.role === 'owner' && currentUser?.email_verified === false) {
          setBusinessSettings(null);
          setPermissions(null);
        } else {
          await fetchBusinessSettings();
          await fetchPermissions();
        }
      } catch (error) {
        if (error?.response?.status === 402 || error?.response?.status === 403) {
          const storedUser = readStoredAuthUser();
          if (storedUser) {
            setUser(storedUser);
            setBusinessSettings(null);
            setPermissions(null);
            setWorkspaceAccessError({
              status: error.response.status,
              message: getErrorMessage(error, 'Your workspace is temporarily unavailable.'),
            });
          } else {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            persistAuthUser(null);
            setUser(null);
            setBusinessSettings(null);
            setPermissions(null);
            setWorkspaceAccessError(null);
          }
        } else {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          persistAuthUser(null);
          setUser(null);
          setBusinessSettings(null);
          setPermissions(null);
          setWorkspaceAccessError(null);
        }
      }
    }
    setLoading(false);
  }, [api, fetchBusinessSettings, fetchPermissions]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    const response = await axios.post(`${API_BASE}/api/auth/login`, formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    persistAuthUser(userData);
    setUser(userData);
    setWorkspaceAccessError(null);
    if (userData?.role === 'owner' && userData?.email_verified === false) {
      setBusinessSettings(null);
      setPermissions(null);
    } else {
      await fetchBusinessSettings();
      await fetchPermissions();
    }
    return userData;
  }, [fetchBusinessSettings, fetchPermissions]);

  const register = useCallback(async (email, password, fullName) => {
    const response = await api.post('/auth/register', {
      email,
      password,
      full_name: fullName,
    });
    return response.data;
  }, [api]);

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    persistAuthUser(null);
    setUser(null);
    setBusinessSettings(null);
    setPermissions(null);
    setWorkspaceAccessError(null);
  }, []);

  const updateBusinessSettings = useCallback(async (settings) => {
    const response = await api.post('/settings/business', settings);
    setBusinessSettings(response.data);
    return response.data;
  }, [api]);

  const isOwner = user?.role === 'owner';
  const isManager = user?.role === 'manager' || isOwner;
  const isCashier = user?.role === 'cashier' || isManager;
  const isSetupComplete = businessSettings?.is_setup_complete;

  const value = useMemo(() => ({
    user,
    loading,
    businessSettings,
    permissions,
    workspaceAccessError,
    login,
    register,
    logout,
    api,
    isOwner,
    isManager,
    isCashier,
    isSetupComplete,
    updateBusinessSettings,
    fetchBusinessSettings,
    fetchPermissions,
    checkAuth,
  }), [user, loading, businessSettings, permissions, workspaceAccessError, login, register, logout, api, isOwner, isManager, isCashier, isSetupComplete, updateBusinessSettings, fetchBusinessSettings, fetchPermissions, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
